import useAnalytics from '@hooks/useAnalytics';
import { useIsChooseDoctorFlow } from '@hooks/useIsChooseDoctorFlow';
import { fetchPractitioners } from '@packages/cortex-sdk';
import {
  useClinicStore,
  useNavigationStore,
  useServiceTreeStore,
} from '@packages/shared';
import { Actions, Practitioner } from '@packages/types';
import {
  AnimatedStep,
  Divider,
  SpinnerWrapper,
  StepButton,
  StepWrapper,
} from '@packages/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';

import SelectGPPractitionerStep from './SelectGPPractitionerStep';

export default function SelectPractitionerStep() {
  const { t } = useTranslation('booking');
  const setEventProps = useAnalytics();

  const { queries, advanceStep, setQueries } = useNavigationStore(
    (state) => ({
      setQueries: state.setQueries,
      advanceStep: state.advanceStep,
      queries: state.queries,
    }),
    shallow,
  );
  const isChooseGpPractitionerService = useIsChooseDoctorFlow();

  useEffect(() => {
    const displayStep =
      queries.vertical === 'mental-health' || isChooseGpPractitionerService;
    if (!displayStep) {
      advanceStep();
    }
  }, [queries.vertical, isChooseGpPractitionerService, advanceStep]);

  const nextAvailableMap = useRef(new Map<string, number>());

  const { clinic } = useClinicStore()((state) => ({
    clinic: state.clinic,
  }));

  const { getServiceUuid } = useServiceTreeStore(
    (state) => ({
      getServiceUuid: state.getServiceUuid,
    }),
    shallow,
  );

  const serviceUuid = queries.service
    ? getServiceUuid(queries.service) ?? ''
    : '';

  const { data: practitioners, isLoading } = useQuery(
    ['practitioners', nextAvailableMap],
    ({ signal }) =>
      fetchPractitioners({
        serviceId: serviceUuid,
        clinicId: clinic?.id,
        signal,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!serviceUuid,
      keepPreviousData: true,
      select: (data) => {
        return data;
      },
    },
  );

  if (isChooseGpPractitionerService) {
    return <SelectGPPractitionerStep />;
  }
  const handleClick = (
    practitioner?: Practitioner,
    practitionerIndex?: number,
  ) => {
    if (practitioner) {
      setQueries({ practitionerId: practitioner.id });

      setEventProps({
        event: {
          action: Actions.SelectPractitioner,
          vertical: queries.vertical,
          speciality: queries.speciality,
          service: queries.service,
          clinic: clinic?.shortId,
          practitionerId: practitioner.id,
          practitionerNumber: practitionerIndex
            ? practitionerIndex + 1
            : undefined,
        },
      });
    } else {
      setEventProps({
        event: {
          action: Actions.SkipSelectPractitioner,
          vertical: queries.vertical,
          speciality: queries.speciality,
          service: queries.service,
          clinic: clinic?.shortId,
        },
      });
    }

    advanceStep();
  };

  return (
    <StepWrapper
      isLoading={isLoading}
      stepWidth="max-w-[512px]"
      title={t('steps.select_practitioner.title')}
    >
      <AnimatedStep order={0}>
        <StepButton
          title={
            <h2 className="text-black">
              {t('steps.select_practitioner.see_all_available_appointments')}
            </h2>
          }
          className="mb-3 py-8"
          onClick={() => {
            handleClick();
          }}
        />

        <Divider
          text={t('steps.select_practitioner.or_choose')}
          className="mb-10 mt-12"
        />
      </AnimatedStep>
      {isLoading ? (
        <SpinnerWrapper />
      ) : (
        practitioners?.map((practitioner: Practitioner, index: number) => (
          <AnimatedStep key={practitioner.id} order={index}>
            <StepButton
              title={
                <div className="flex h-full w-full flex-row items-center">
                  <p className="text-md text-black">{practitioner.name}</p>
                </div>
              }
              onClick={() => {
                handleClick(practitioner, index);
              }}
              imageSrc={
                practitioner.photoUrl || '/illustrations/square-doctor.png'
              }
              className="group"
            />
          </AnimatedStep>
        ))
      )}
    </StepWrapper>
  );
}
